/* tol-header-mega-hover.js — メガメニューの hover 展開（2026-06-11 ユーザー裁定・同日 FB で改修）
 *
 * 凍結 top_c_mobile_clean.html L73 `.mega:hover .mega-panel{display:grid}` の hover 展開を
 * Dawn の details/summary 構造へ追加する。click / キーボード操作は Dawn 既存のまま維持
 * （S10 ADR「凍結 hover-only は不採用」の a11y 根拠と両立するハイブリッド。hover は追加経路）。
 *
 * 契約:
 * - 対象 = .header__inline-menu 内の header-menu > details.mega-menu のみ
 *   （menu_type_desktop=dropdown・SP ドロワー・言語セレクタは非対象）。
 * - 990px 以上かつ hover/pointer:fine の環境のみ作動（タッチ・SP は Dawn click のまま）。
 * - open は details.open プロパティ経由 → toggle イベントが発火し HeaderMenu.onToggle
 *   （header.preventHide / --header-bottom-position-desktop）が click 時と同経路で走る。
 * - aria-expanded は自前同期する（global.js の Details- binder は click 時しか同期しない）。
 * - hover で開くときは「他の開いている mega を必ず閉じる」（FB#1: click で開いた details は
 *   focus が summary に残り、自身の pointerleave ガードでは閉じない＝複数同時開きの原因。
 *   Dawn の click 経路は focusout で排他になるが hover はフォーカスが動かないため明示排他が要る）。
 * - 自身の pointerleave の close には 180ms の猶予（パネルは absolute top:100% でナビ項目との間に
 *   数 px の帯があるため。0713 #9 で 120→180ms・依頼レンジ 150〜200 の中央値）。
 *   隣接項目への切替は猶予を待たず排他クローズで即時（FB#2）。
 * - フォーカスガードは「キーボード由来（:focus-visible）」に限定（0713 #9・2026-07-31）:
 *   旧「details 内に focus がある間は閉じない」は click 起点（focus が summary に残る）で
 *   永久に閉じないバグの原因だった（DEV 実機で再現確定）。:focus-visible 非対応エンジンは
 *   matches() が throw するため feature-detect し、global.js の focusVisiblePolyfill が付与する
 *   .focused クラス判定へフォールバック（S10 ADR「click/キーボード a11y 維持」は不変）。
 * - ✕ボタン（0713 #9・snippets/tol-mega-close-btn.liquid・[data-tol-mega-close]）: click で
 *   close() ＋ summary へ focus 復帰。mq 非ゲート（≥990 の touch 環境は hover 不作動でも
 *   Dawn click で開けるため✕が唯一の視覚的な閉じ手段）。button は summary 外＝native toggle
 *   と二重発火しない。close() の open=false 代入で HeaderMenu.onToggle は通常経路で走る。
 * - bfcache 初期化（0713 #7）: pageshow(e.persisted) と初期実行（defer＝DOM 完成後）で
 *   全 mega を閉状態へ（open 除去・.tol-mega-closing/タイマー掃除・aria-expanded 常時同期）。
 *   ブラウザバックで「開いたまま復元」を防ぐ。mq 非依存（bfcache 復元は入力種別を問わない）。
 * - 開閉アニメーション（FB#3・ユーザー裁定: 凍結は display 切替の無アニメだが唐突感低減で追加）:
 *   open は CSS の [open] アニメーション（tol-layout.css §2・click 経路にも自然適用）。
 *   close は native details の open 除去が即時非表示のため、.tol-mega-closing を付与して
 *   退場アニメーション後に open を外す（本 JS 経路のみ。Esc/focusout の Dawn close は即時のまま）。
 *   prefers-reduced-motion では両方向とも無アニメ（CSS 側と二重に保証）。
 */
(() => {
  const mq = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CLOSE_GRACE_MS = 180; // 0713 #9（120→180・依頼レンジ 150〜200 の中央値）
  const CLOSE_ANIM_MS = 200; // tol-layout.css §2 の tol-mega-close（0.18s）+ マージン

  // :focus-visible の feature-detect（非対応エンジンは matches() が throw ＝ガードが
  // 永久 return して #9 のバグが残るため。global.js:135-142 と同型）。
  let focusVisibleSupported = true;
  try {
    document.querySelector(':focus-visible');
  } catch {
    focusVisibleSupported = false;
  }
  const isKeyboardFocus = (el) =>
    focusVisibleSupported ? el.matches(':focus-visible') : el.classList.contains('focused');

  const items = [];
  document.querySelectorAll('.header__inline-menu header-menu').forEach((headerMenu) => {
    const details = headerMenu.querySelector('details.mega-menu');
    if (!details) return;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    const closeBtn = details.querySelector('[data-tol-mega-close]');
    items.push({ headerMenu, details, summary, closeBtn, graceTimer: null, animTimer: null });
  });
  if (items.length === 0) return;

  const clearTimers = (item) => {
    clearTimeout(item.graceTimer);
    clearTimeout(item.animTimer);
    item.graceTimer = null;
    item.animTimer = null;
  };

  const finishClose = (item) => {
    item.details.classList.remove('tol-mega-closing');
    item.details.open = false;
    item.summary.setAttribute('aria-expanded', 'false');
  };

  const close = (item) => {
    clearTimers(item);
    if (!item.details.open) {
      item.details.classList.remove('tol-mega-closing');
      return;
    }
    if (reducedMotion.matches) {
      finishClose(item);
      return;
    }
    // 既に退場中でも必ず再スケジュールする（classList.add は冪等・アニメは継続）。
    // 冒頭の clearTimers が進行中の animTimer を消すため、ここで早期 return すると
    // finishClose が孤児化し「open=true なのに opacity 0」のゴーストパネルが残る
    // （高速 hover A→B の排他クローズ再入で実機再現・Copilot G2 捕捉 2026-06-12）。
    item.details.classList.add('tol-mega-closing');
    item.animTimer = setTimeout(() => finishClose(item), CLOSE_ANIM_MS);
  };

  const open = (item) => {
    clearTimers(item);
    // 退場アニメーション中の再 hover は開いたまま留める（open 属性は未除去）
    item.details.classList.remove('tol-mega-closing');
    items.forEach((other) => {
      if (other !== item) close(other);
    });
    if (item.details.open) return;
    item.details.open = true;
    item.summary.setAttribute('aria-expanded', 'true');
  };

  // 0713 #7: bfcache 復元・初期表示で必ず閉状態から始める（冪等・mq 非依存）。
  const resetAll = () => {
    items.forEach((item) => {
      clearTimers(item);
      item.details.classList.remove('tol-mega-closing');
      // bfcache 復元でブラウザが details 内リンクへ focus を戻すことがある＝閉じると
      // display:none 内で focus が孤児化するため summary へ退避（Codex レビュー 2026-07-31）
      if (item.details.open && item.details.contains(document.activeElement)) {
        item.summary.focus();
      }
      item.details.open = false; // 閉でも代入は無害（toggle は状態変化時のみ発火）
      item.summary.setAttribute('aria-expanded', 'false'); // open に依らず常時同期
    });
  };
  resetAll(); // defer 実行＝DOM 完成後＝依頼イメージの DOMContentLoaded 相当
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetAll();
  });

  items.forEach((item) => {
    item.headerMenu.addEventListener('pointerenter', () => {
      if (!mq.matches) return;
      open(item);
    });

    item.headerMenu.addEventListener('pointerleave', () => {
      if (!mq.matches || !item.details.open) return;
      clearTimeout(item.graceTimer);
      item.graceTimer = setTimeout(() => {
        item.graceTimer = null;
        if (!item.details.open) return;
        // キーボード由来の focus のみ閉じない（0713 #9: click 起点の focus 残留では閉じる）
        const activeEl = document.activeElement;
        if (activeEl && item.details.contains(activeEl) && isKeyboardFocus(activeEl)) return;
        close(item);
      }, CLOSE_GRACE_MS);
    });

    // 0713 #9: ✕ボタン＝手動で閉じる手段（hover 経路と独立・touch PC でも機能）
    if (item.closeBtn) {
      item.closeBtn.addEventListener('click', () => {
        close(item);
        item.summary.focus(); // focus 迷子防止（closing アニメ中も summary は可視・キーボード文脈の連続性）
      });
    }
  });
})();
